import { createContext, useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { getFirestore } from 'firebase/firestore'
import { app } from '@configuration/firebaseConfig'
import { getSportsLeaguesData } from './services/getSportsLeaguesData'
import { getEventsAndTeamsData } from './services/getEventsAndTeamsData'
import { getActiveTournamentPools } from './services/getActiveTournamentPools'
import { getTeamsData } from './services/getTeamsData'
import { postTournamentPool, patchTournamentPool } from './services/tournamentPoolService'
import { getUserTournamentEntry, getTournamentKey } from './services/userTournamentEntryService'
import { patchUserTournamentEntryEvent } from './services/userTournamentEntryService'
import { postUserTournamentEntry } from './services/userTournamentEntryService'
import { logger } from '@helpers/logger'
import { getCompletedTournamentPools } from './services/getCompletedTournamentPools'
import { postEventResult } from './services/eventsResultsService'
import { postFeaturedTournamentPoolsSummary, getFeaturedTournamentPoolsSummary, setBannerAd, removeAllBannerAds } from './services/featuredPoolsService'
import { TranslationContext } from '@providers/TranslationContext'
import { getUserCount, getLoginData } from './services/analyticsService'
import { useAuth } from './hooks/useAuth'
import { postAppImage } from './services/postAppImage'
import { getSportDbEvents } from './services/getSportDbEvents'
import { getTournamentAnalytics } from './services/tournamentAnalyticsService'
import { linkEventToSportDb, addEventToLeague } from './services/eventAdmin'
import { teamAdmin } from './services/teamAdminService'
import { getSelectedLeaguesAdmin, patchLeaguesAdmin } from './services/leaguesAdminService'
import { getSportsDbLeagues } from './services/sportsDbLeaguesService'
import { getSportsDbSportsDataServiceLogs, getSportsDbSportsDataServiceLog, getSportsDbSportsDataProcessingStatus, startSportsDbSportsDataProcessing } from './services/sportsDbSportsDataServiceLogsService'
import { getGolfActiveTournament, getGolfEligiblePlayers, postGolfPicks, getGolfPicks, getGolfLeaderboard, getGolfTournaments, postGolfPool, getGolfPool, getGolfPoolsPublic, postGolfPoolJoin, getGolfPoolLeaderboard, getGolfPoolPendingMembers, approveGolfPoolMember, denyGolfPoolMember } from './services/golfService'

// eslint-disable-next-line react-refresh/only-export-components
export const DataContext = createContext()

export const DataProvider = ({ children, setActivePage }) => {
  const userRef = useRef(null)
  const [userDoc, setUserDoc] = useState(null)
  const [error, setError] = useState(null)
  const [checkedAuthenticated, setCheckedAuthenticated] = useState(false)
  const [leaguesData, setLeaguesData] = useState({ sports: {} })
  const [teamsData, setTeamsData] = useState({ sports: {} })
  const dataFetchedRef = useRef(false)
  const [activeTournamentPoolData, setActiveTournamentPoolData] = useState(undefined)
  const [foundTournamentPools, setFoundTournamentPools] = useState(undefined)
  const [hasActiveTournaments, setHasActiveTournaments] = useState(true)
  const [hasCompletedTournaments, setHasCompletedTournaments] = useState(true)
  const [golfTournament, setGolfTournament] = useState(null)
  const [golfPlayers, setGolfPlayers] = useState([])
  const [golfUserEntry, setGolfUserEntry] = useState(null)
  const [golfUserEntries, setGolfUserEntries] = useState({})
  const [golfDataLoaded, setGolfDataLoaded] = useState(false)
  const [golfLeaderboard, setGolfLeaderboard] = useState(null)
  const [golfLeaderboards, setGolfLeaderboards] = useState({})
  const [golfTournaments, setGolfTournaments] = useState([])
  const [activeTournamentKey, setActiveTournamentKey] = useState(null)
  const [selectedGolfTournament, setSelectedGolfTournament] = useState(null)
  const [changeTeamMode, setChangeTeamMode] = useState(false)
  const [golfPools, setGolfPools] = useState([])
  const [golfPublicPools, setGolfPublicPools] = useState([])
  const [selectedGolfPool, setSelectedGolfPool] = useState(null)
  const [golfPoolLeaderboards, setGolfPoolLeaderboards] = useState({})
  const [featuredTournamentPoolsSummaryData, setFeaturedTournamentPoolsSummaryData] = useState(undefined)
  const [completedTournamentPoolData, setCompletedTournamentPoolData] = useState(undefined)
  const [selectedUserTournamentEntry, setSelectedUserTournamentEntry] = useState(undefined)
  const [reloadTournamentData, setReloadTournamentData] = useState(true)
  const [selectedSearchSport, setSelectedSearchSport] = useState(undefined)
  const [loadingEventKeys, setLoadingEventKeys] = useState({})
  const [analyticsData, setAnalyticsData] = useState({ totalUsers: 0 })
  const [loginData, setLoginData] = useState({ daily: null, weekly: null, monthly: null, yearly: null })
  const [selectedPeriod, setSelectedPeriod] = useState('daily')
  const [tournamentAnalyticsData, setTournamentAnalyticsData] = useState([])
  const [isChatLoaded, setIsChatLoaded] = useState(false)
  const [isChatVisible, setIsChatVisible] = useState(false)

  const db = getFirestore(app)
  const globalStartTime = useRef(Date.now())
  const getSportsLeaguesDataRef = useRef(() => { })
  const { locale, changeLocale } = useContext(TranslationContext)

  useEffect(() => {
    getSportsLeaguesDataRef.current = () => {
      getSportsLeaguesData(db, leaguesData, setLeaguesData, () => {
        dataFetchedRef.current = true
      })
    }
  }, [db, leaguesData])

  const { userRef: authUserRef, showLoginScreen } = useAuth({
    locale,
    changeLocale,
    setActivePage,
    setUserDoc,
    setActiveTournamentPoolData,
    setCompletedTournamentPoolData,
    setLeaguesData,
    setFeaturedTournamentPoolsSummaryData,
    setHasActiveTournaments,
    setHasCompletedTournaments,
    setError,
    setCheckedAuthenticated,
    getSportsLeaguesDataRef,
    dataFetchedRef,
    isChatLoaded,
    setIsChatLoaded,
  })

  useEffect(() => {
    userRef.current = authUserRef.current
  }, [authUserRef.current]) // eslint-disable-line

  useEffect(() => {
    if (userDoc) {
      getGolfActiveTournament().then(tournament => {
        if (tournament?.tournament) {
          setGolfTournament(tournament)
          const key = tournament.tournament.tournamentKey
          setActiveTournamentKey(key)
          getGolfPicks(key).then(entry => {
            if (entry) setGolfUserEntry(entry)
          })
          getGolfLeaderboard(key).then(result => {
            if (result) setGolfLeaderboard(result)
          })
          getGolfPool(key).then(result => {
            const newPools = result?.pools || []
            setGolfPools(prev => {
              const existingIds = prev.map(p => p.poolId)
              const toAdd = newPools.filter(p => !existingIds.includes(p.poolId))
              return [...prev, ...toAdd]
            })
          })
        }
      })
      getGolfTournaments().then(result => {
        const tournaments = result?.tournaments || []
        setGolfTournaments(tournaments)
        // Fetch pools and picks for all tournaments
        Promise.all(tournaments.map(t => getGolfPool(t.tournamentKey))).then(results => {
          const allPools = results.flatMap(r => r?.pools || [])
          setGolfPools(prev => {
            const existingIds = prev.map(p => p.poolId)
            const newPools = allPools.filter(p => !existingIds.includes(p.poolId))
            return [...prev, ...newPools]
          })
        })
        // Fetch picks for all tournaments
        Promise.all(tournaments.map(t => getGolfPicks(t.tournamentKey).then(entry => ({ key: t.tournamentKey, entry })))).then(results => {
          const entriesMap = {}
          results.forEach(({ key, entry }) => {
            if (entry?.golfEntry?.userPicks) entriesMap[key] = entry
          })
          setGolfUserEntries(entriesMap)
          setGolfDataLoaded(true)
        })
      })
    }
  }, [userDoc]) // eslint-disable-line

  const handleError = (error, context) => {
    const elapsedSeconds = ((Date.now() - globalStartTime.current) / 1000).toFixed(2)
    logger.error(`[activeTournamentPoolsLoading][${elapsedSeconds}s] Error in ${context}:`, error)
    setError(error instanceof Error ? error : new Error(error?.message || 'Unknown error'))
  }

  const getGolfData = async () => {
    const [tournament, players, entry] = await Promise.all([
      getGolfActiveTournament(),
      getGolfEligiblePlayers(),
      activeTournamentKey ? getGolfPicks(activeTournamentKey) : null,
    ])
    setGolfTournament(prev => selectedGolfTournament || tournament || prev)
    setGolfPlayers(players?.players || [])
    if (entry) setGolfUserEntry(entry)
  }

  const postGolfPicksData = async (tournamentKey, picks) => {
    return await postGolfPicks(tournamentKey, picks)
  }

  const getGolfLeaderboardData = async (tournamentKey) => {
    const result = await getGolfLeaderboard(tournamentKey)
    setGolfLeaderboard(result)
    if (tournamentKey) setGolfLeaderboards(prev => ({ ...prev, [tournamentKey]: result }))
    return result
  }

  const getGolfTournamentsData = async () => {
    const result = await getGolfTournaments()
    setGolfTournaments(result?.tournaments || [])
    return result
  }

  const postGolfPoolData = async (poolData) => {
    return await postGolfPool(poolData)
  }

  const getGolfPoolData = async (tournamentKey) => {
    const result = await getGolfPool(tournamentKey)
    const newPools = result?.pools || []
    setGolfPools(prev => {
      const existingIds = prev.map(p => p.poolId)
      const toAdd = newPools.filter(p => !existingIds.includes(p.poolId))
      return [...prev, ...toAdd]
    })
    return result
  }

  const getGolfPublicPoolsData = async (tournamentKey) => {
    const result = await getGolfPoolsPublic(tournamentKey)
    setGolfPublicPools(result?.pools || [])
    return result
  }
  
  const postGolfPoolJoinData = async (poolId, inviteKey) => {
    return await postGolfPoolJoin(poolId, inviteKey)
  }

  const getGolfPoolPendingMembersData = async (poolId) => {
    return await getGolfPoolPendingMembers(poolId)
  }

  const approveGolfPoolMemberData = async (poolId, memberUid) => {
    return await approveGolfPoolMember(poolId, memberUid)
  }

  const denyGolfPoolMemberData = async (poolId, memberUid) => {
    return await denyGolfPoolMember(poolId, memberUid)
  }

  const getGolfPoolLeaderboardData2 = async (poolId) => {
    const result = await getGolfPoolLeaderboard(poolId)
    setGolfPoolLeaderboards(prev => ({ ...prev, [poolId]: result }))
    return result
  }

  const getEventsAndTeamsDataCallback = useCallback(
    async (league, strCurrentSeason, sport, isResultsPage, forceRefresh = false) => {
      try {
        return getEventsAndTeamsData(db, leaguesData, setLeaguesData, teamsData, setTeamsData, league, strCurrentSeason, sport, isResultsPage, forceRefresh)
      } catch (error) {
        handleError(error, 'getEventsAndTeamsData')
      }
    },
    [db, leaguesData, teamsData],
  )

  const patchUserTournamentEntryEventCallback = useCallback(
    async (tournamentKey, eventKey, predictionKey, setReloadTournamentData) => {
      try {
        setLoadingEventKeys((prevState) => ({ ...prevState, [eventKey]: true }))
        const patchResult = await patchUserTournamentEntryEvent(tournamentKey, eventKey, predictionKey)
        const userTournamentEntry = await getUserTournamentEntry(selectedUserTournamentEntry.tournamentKey)
        setSelectedUserTournamentEntry(userTournamentEntry)
        const updatedUserEntry = { ...selectedUserTournamentEntry }
        updatedUserEntry.userEntry.userTournamentPoolEntryEvents = updatedUserEntry.userEntry.userTournamentPoolEntryEvents.map((event) => {
          if (event.eventKey === eventKey) {
            return { ...event, ...userTournamentEntry.userEntry.userTournamentPoolEntryEvents.find((updatedEvent) => updatedEvent.eventKey === eventKey) }
          }
          return event
        })
        setSelectedUserTournamentEntry(updatedUserEntry)
        setActiveTournamentPoolData((prevData) => {
          if (prevData) {
            const updatedData = prevData.map((entry) => {
              if (entry.tournamentKey === tournamentKey) {
                const updatedEntry = { ...entry }
                updatedEntry.userEntry.userTournamentPoolEntryEvents = updatedEntry.userEntry.userTournamentPoolEntryEvents.map((event) => {
                  if (event.eventKey === eventKey) {
                    return { ...event, ...userTournamentEntry.userEntry.userTournamentPoolEntryEvents.find((updatedEvent) => updatedEvent.eventKey === eventKey) }
                  }
                  return event
                })
                return updatedEntry
              }
              return entry
            })
            return [...updatedData]
          }
          return prevData
        })
        setLoadingEventKeys((prevState) => ({ ...prevState, [eventKey]: false }))
        setReloadTournamentData(true)
        return patchResult.predictionKey
      } catch (error) {
        handleError(error, 'patchUserTournamentEntryEvent')
      } finally {
        setLoadingEventKeys((prevState) => ({ ...prevState, [eventKey]: false }))
      }
    },
    [selectedUserTournamentEntry],
  )

  const postUserTournamentEntryCallback = useCallback(
    async (tournamentKey, inviteKey) => {
      const createdUserTournamentEntry = await postUserTournamentEntry(tournamentKey, inviteKey)
      createdUserTournamentEntry.joined = true
      setSelectedUserTournamentEntry(createdUserTournamentEntry)
      if (activeTournamentPoolData) {
        const filteredData = activeTournamentPoolData.filter(item => item.tournamentKey !== createdUserTournamentEntry.tournamentKey)
        filteredData.unshift(createdUserTournamentEntry)
        setActiveTournamentPoolData([...filteredData])
      } else {
        setActiveTournamentPoolData([createdUserTournamentEntry])
      }
      setHasActiveTournaments(true)
    },
    [activeTournamentPoolData],
  )

  const patchTournamentPoolCallback = useCallback(
    async (tournamentKey, updates) => {
      try {
        const updatedUserTournamentEntry = await patchTournamentPool(tournamentKey, updates)
        setSelectedUserTournamentEntry(updatedUserTournamentEntry)
        setActiveTournamentPoolData(prevData => {
          if (!prevData) return prevData
          return prevData.map(entry => {
            if (entry.tournamentKey === tournamentKey) {
              return { ...entry, tournamentData: { ...entry.tournamentData, ...updates, tournamentBannerImage: updates.tournamentBannerImage || entry.tournamentData.tournamentBannerImage, tournamentBannerBackgroundColor: updates.tournamentBannerBackgroundColor || entry.tournamentData.tournamentBannerBackgroundColor } }
            }
            return entry
          })
        })
        setCompletedTournamentPoolData(prevData => {
          if (!prevData) return prevData
          return prevData.map(entry => {
            if (entry.tournamentKey === tournamentKey) {
              return { ...entry, tournamentData: { ...entry.tournamentData, ...updates, tournamentBannerImage: updates.tournamentBannerImage || entry.tournamentData.tournamentBannerImage, tournamentBannerBackgroundColor: updates.tournamentBannerBackgroundColor || entry.tournamentData.tournamentBannerBackgroundColor } }
            }
            return entry
          })
        })
        return updatedUserTournamentEntry
      } catch (error) {
        logger.error('Error in patchTournamentPoolCallback:', error)
        throw error
      }
    },
    [],
  )

  const getActiveTournamentPoolsCallback = useCallback(async () => {
    if (userRef.current && Object.keys(leaguesData.sports).length > 0) {
      try {
        await getActiveTournamentPools(featuredTournamentPoolsSummaryData, (userTournamentEntry) => {
          setActiveTournamentPoolData((prevData) => {
            if (prevData) {
              if (prevData?.length <= 0 && !userTournamentEntry) { setHasActiveTournaments(false); return [] }
              setHasActiveTournaments(true)
              const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
              return [...prevData, ...newEntries]
            } else {
              if (!userTournamentEntry) { setHasActiveTournaments(false); return [] }
              setHasActiveTournaments(true)
              const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
              return newEntries
            }
          })
        }, setHasActiveTournaments)
      } finally { }
    } else {
      if (!userRef.current) console.error('No user is authenticated.')
      setActiveTournamentPoolData([])
      setHasActiveTournaments(false)
    }
  }, [leaguesData, featuredTournamentPoolsSummaryData, hasActiveTournaments])

  const getCompetedTournamentPoolsCallback = useCallback(async () => {
    if (userRef.current && Object.keys(leaguesData.sports).length > 0) {
      try {
        await getCompletedTournamentPools((userTournamentEntry) => {
          setCompletedTournamentPoolData((prevData) => {
            if (prevData) {
              if (prevData?.length <= 0 && !userTournamentEntry) { setHasCompletedTournaments(false); return [] }
              setHasCompletedTournaments(true)
              const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
              return newEntries.filter(entry => entry && entry.tournamentData)
            } else {
              if (!userTournamentEntry) { setHasCompletedTournaments(false); return [] }
              setHasCompletedTournaments(true)
              const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
              return newEntries.filter(entry => entry && entry.tournamentData)
            }
          })
        }, setHasCompletedTournaments)
      } finally { }
    } else {
      if (!userRef.current) console.error('No user is authenticated.')
      setCompletedTournamentPoolData([])
      setHasCompletedTournaments(false)
    }
  }, [leaguesData, hasCompletedTournaments])

  const postTournamentPoolCallback = useCallback(
    async (tournamentData) => {
      try {
        if (!userRef.current) throw new Error('No user is authenticated.')
        const userTournamentEntry = await postTournamentPool(tournamentData)
        userTournamentEntry.joined = true
        setSelectedUserTournamentEntry({ ...userTournamentEntry })
        if (activeTournamentPoolData) {
          activeTournamentPoolData.unshift(userTournamentEntry)
          setActiveTournamentPoolData([...activeTournamentPoolData])
        } else {
          setActiveTournamentPoolData([userTournamentEntry])
        }
        setHasActiveTournaments(true)
      } catch (error) {
        handleError(error, 'postTournamentPool')
      }
    },
    [activeTournamentPoolData],
  )

  const activeTournamentPoolDataRef = useRef(activeTournamentPoolData)
  useEffect(() => { activeTournamentPoolDataRef.current = activeTournamentPoolData }, [activeTournamentPoolData])

  const getSelectedUserTournamentEntryCallback = useCallback(async (selectedTournamentPool) => {
    if (!userRef.current) { console.error('No user is authenticated.'); return }
    const currentPoolData = activeTournamentPoolDataRef.current
    if (currentPoolData && Array.isArray(currentPoolData)) {
      const existingTournament = currentPoolData.find(entry => entry.tournamentKey === selectedTournamentPool.tournamentKey)
      if (existingTournament) {
        if (selectedTournamentPool.inviteKey) existingTournament.inviteKey = selectedTournamentPool.inviteKey
        setSelectedUserTournamentEntry(existingTournament)
        return
      }
    }
    const userTournamentEntry = await getUserTournamentEntry(selectedTournamentPool.tournamentKey)
    if (userTournamentEntry) {
      if (selectedTournamentPool.inviteKey) userTournamentEntry.inviteKey = selectedTournamentPool.inviteKey
      setSelectedUserTournamentEntry(userTournamentEntry)
    }
  }, [])

  const postFeaturedTournamentPoolCallback = useCallback(
    async (selectedTournamentPool, featuredPosition, remove = false) => {
      try {
        if (!userRef.current) throw new Error('No user is authenticated.')
        await postFeaturedTournamentPoolsSummary(selectedTournamentPool, featuredPosition, remove)
        try { setFeaturedTournamentPoolsSummaryData(await getFeaturedTournamentPoolsSummary()) } catch (e) { logger.error('Error refreshing featured data:', e) }
        return true
      } catch (error) { handleError(error, 'postFeaturedTournamentPool') }
    }, [],
  )

  const setBannerAdCallback = useCallback(
    async (selectedTournamentPool, featuredPosition, remove = false) => {
      try {
        if (!userRef.current) throw new Error('No user is authenticated.')
        await setBannerAd(selectedTournamentPool, featuredPosition, remove)
        try { setFeaturedTournamentPoolsSummaryData(await getFeaturedTournamentPoolsSummary()) } catch (e) { logger.error('Error refreshing featured data:', e) }
        return true
      } catch (error) { handleError(error, 'setBannerAd') }
    }, [],
  )

  const removeAllBannerAdsCallback = useCallback(
    async () => {
      try {
        if (!userRef.current) throw new Error('No user is authenticated.')
        await removeAllBannerAds()
        try { setFeaturedTournamentPoolsSummaryData(await getFeaturedTournamentPoolsSummary()) } catch (e) { logger.error('Error refreshing featured data:', e) }
        return true
      } catch (error) { handleError(error, 'removeAllBannerAds') }
    }, [],
  )

  const setSelectedUserTournamentEntryCallback = useCallback(async (userTournamentEntry) => {
    if (userRef.current) { setSelectedUserTournamentEntry(userTournamentEntry) } else { console.error('No user is authenticated.') }
  }, [])

  const getTeamsDataCallback = useCallback(
    async (sport, league, strCurrentSeason) => {
      try {
        if (!userRef.current || !db) throw new Error('No user is authenticated or database is not initialized.')
        return await getTeamsData(db, sport, league, strCurrentSeason, teamsData, setTeamsData)
      } catch (error) { handleError(error, 'getTeamsData') }
    },
    [db, teamsData],
  )

  const getTournamentKeyCallback = useCallback(async (entryKey) => {
    if (!userRef.current) { console.error('No user is authenticated.'); return }
    try {
      const response = await getTournamentKey(entryKey)
      return response.tournamentKey
    } catch (error) { handleError(error, 'getTournamentKey') }
  }, [])

  const getUserCountCallback = useCallback(async () => {
    try { setAnalyticsData(await getUserCount()) } catch (error) { handleError(error, 'getUserCount') }
  }, [])

  const getLoginDataCallback = useCallback(async (period, limit = 30) => {
    try {
      const data = await getLoginData(period, limit)
      if (!data) return
      setLoginData(prev => ({ ...prev, [period]: Array.isArray(data) ? data : [data] }))
    } catch (error) { logger.error('getLoginData', error) }
  }, [])

  const getTournamentAnalyticsCallback = useCallback(async () => {
    try {
      const result = await getTournamentAnalytics()
      setTournamentAnalyticsData(result)
      return result
    } catch (error) { handleError(error, 'getTournamentAnalytics') }
  }, [])

  const postAppImageFile = useCallback(async (path, imageFile) => {
    try { return (await postAppImage(path, imageFile)).imageUrl } catch (error) { handleError(error, 'postAppImage') }
  }, [])

  const teamAdminCallback = useCallback(async (teamData) => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await teamAdmin(teamData)
    } catch (error) { handleError(error, 'teamAdmin'); throw error }
  }, [])

  const getSportsDbLeaguesCallback = useCallback(async (sport) => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await getSportsDbLeagues(sport)
    } catch (error) { handleError(error, 'getSportsDbLeagues'); throw error }
  }, [])

  const patchLeaguesAdminCallback = useCallback(async (leaguesToProcess) => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await patchLeaguesAdmin(leaguesToProcess)
    } catch (error) { handleError(error, 'patchLeaguesAdmin'); throw error }
  }, [])

  const getSelectedLeaguesAdminCallback = useCallback(async () => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await getSelectedLeaguesAdmin()
    } catch (error) { handleError(error, 'getSelectedLeaguesAdmin'); throw error }
  }, [])

  const getSportsDbSportsDataServiceLogsCallback = useCallback(async (page = 1, limit = 10) => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await getSportsDbSportsDataServiceLogs(page, limit)
    } catch (error) { throw error }
  }, [])

  const getSportsDbSportsDataServiceLogCallback = useCallback(async (logId) => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await getSportsDbSportsDataServiceLog(logId)
    } catch (error) { throw error }
  }, [])

  const getSportsDbSportsDataProcessingStatusCallback = useCallback(async () => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await getSportsDbSportsDataProcessingStatus()
    } catch (error) { throw error }
  }, [])

  const startSportsDbSportsDataProcessingCallback = useCallback(async () => {
    try {
      if (!userRef.current) throw new Error('No user is authenticated.')
      return await startSportsDbSportsDataProcessing()
    } catch (error) { throw error }
  }, [])

  const contextValue = useMemo(
    () => ({
      user: userRef.current,
      userDoc,
      db,
      setUserDoc,
      leaguesData,
      getEventsAndTeamsData: getEventsAndTeamsDataCallback,
      postTournamentPool: postTournamentPoolCallback,
      getActiveTournamentPools: getActiveTournamentPoolsCallback,
      getCompetedTournamentPools: getCompetedTournamentPoolsCallback,
      getSelectedUserTournamentEntry: getSelectedUserTournamentEntryCallback,
      setSelectedUserTournamentEntry: setSelectedUserTournamentEntryCallback,
      postFeaturedTournamentPoolsSummary: postFeaturedTournamentPoolCallback,
      setBannerAd: setBannerAdCallback,
      removeAllBannerAds: removeAllBannerAdsCallback,
      selectedUserTournamentEntry,
      activeTournamentPoolData,
      featuredTournamentPoolsSummaryData,
      completedTournamentPoolData,
      dataFetched: dataFetchedRef.current,
      checkedAuthenticated,
      getTeamsData: getTeamsDataCallback,
      patchUserTournamentEntryEvent: patchUserTournamentEntryEventCallback,
      postUserTournamentEntry: postUserTournamentEntryCallback,
      patchTournamentPool: patchTournamentPoolCallback,
      teamsData,
      selectedSearchSport,
      setSelectedSearchSport,
      setFoundTournamentPools,
      foundTournamentPools,
      reloadTournamentData,
      setReloadTournamentData,
      loadingEventKeys,
      postEventResult,
      getTournamentKey: getTournamentKeyCallback,
      hasActiveTournaments,
      hasCompletedTournaments,
      analyticsData,
      loginData,
      selectedPeriod,
      setSelectedPeriod,
      getUserCount: getUserCountCallback,
      getLoginData: getLoginDataCallback,
      isChatLoaded,
      setIsChatLoaded,
      isChatVisible,
      setIsChatVisible,
      showLoginScreen,
      postAppImage: postAppImageFile,
      getSportDbEvents,
      getTournamentAnalytics: getTournamentAnalyticsCallback,
      tournamentAnalyticsData,
      linkEventToSportDb,
      addEventToLeague,
      teamAdmin: teamAdminCallback,
      getSportsDbLeagues: getSportsDbLeaguesCallback,
      patchLeaguesAdmin: patchLeaguesAdminCallback,
      getSelectedLeaguesAdmin: getSelectedLeaguesAdminCallback,
      getSportsDbSportsDataServiceLogs: getSportsDbSportsDataServiceLogsCallback,
      getSportsDbSportsDataServiceLog: getSportsDbSportsDataServiceLogCallback,
      getSportsDbSportsDataProcessingStatus: getSportsDbSportsDataProcessingStatusCallback,
      startSportsDbSportsDataProcessing: startSportsDbSportsDataProcessingCallback,
      golfTournament,
      golfPlayers,
      golfUserEntry,
      golfUserEntries,
      golfDataLoaded,
      golfLeaderboard,
      golfLeaderboards,
      getGolfData,
      postGolfPicksData,
      getGolfLeaderboardData,
      golfTournaments,
      setSelectedGolfPool,
      postGolfPoolData,
      getGolfPoolData,
      getGolfPublicPoolsData,
      postGolfPoolJoinData,
      getGolfPoolPendingMembersData,
      approveGolfPoolMemberData,
      denyGolfPoolMemberData,
      getGolfPoolLeaderboardData2,
      getGolfTournaments: getGolfTournamentsData,
      setActiveTournamentKey,
      activeTournamentKey,
      setSelectedGolfTournament,
      changeTeamMode,
      golfPools,
      golfPublicPools,
      selectedGolfPool,
      golfPoolLeaderboards,
      setChangeTeamMode,
    }),
    [ // eslint-disable-line
      userDoc, leaguesData, selectedUserTournamentEntry, activeTournamentPoolData,
      featuredTournamentPoolsSummaryData, completedTournamentPoolData, checkedAuthenticated,
      teamsData, postFeaturedTournamentPoolCallback, setBannerAdCallback, removeAllBannerAdsCallback,
      getEventsAndTeamsDataCallback, postTournamentPoolCallback, getActiveTournamentPoolsCallback,
      getCompetedTournamentPoolsCallback, getSelectedUserTournamentEntryCallback,
      setSelectedUserTournamentEntryCallback, getTeamsDataCallback, patchTournamentPoolCallback,
      patchUserTournamentEntryEventCallback, postUserTournamentEntryCallback, selectedSearchSport,
      setSelectedSearchSport, setFoundTournamentPools, foundTournamentPools, reloadTournamentData,
      setReloadTournamentData, loadingEventKeys, getTournamentKeyCallback, hasActiveTournaments,
      hasCompletedTournaments, analyticsData, loginData, selectedPeriod, getUserCountCallback,
      getLoginDataCallback, isChatLoaded, setIsChatLoaded, isChatVisible, setIsChatVisible,
      showLoginScreen, postAppImageFile, getTournamentAnalyticsCallback, tournamentAnalyticsData,
      teamAdminCallback, getSportsDbLeaguesCallback, patchLeaguesAdminCallback,
      getSelectedLeaguesAdminCallback, db, golfTournament, golfLeaderboard, selectedGolfTournament,
      activeTournamentKey, changeTeamMode,
    ],
  )

  if (error) throw error

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
}

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  setActivePage: PropTypes.func,
}
